import { useQuery } from 'react-query';
import api from '../services/api';

/**
 * Hook personalizado para obtener alertas de pagos (vencidos y próximos a vencer)
 */
export const usePaymentAlerts = (options = {}) => {
  const { enabled = true, refetchInterval = 5 * 60 * 1000 } = options; // Refetch cada 5 minutos

  // Función auxiliar para verificar si un pago está realmente vencido
  const isOverdue = (dueDate, status) => {
    if (status === 'paid' || status === 'cancelled') return false;
    // Parsear fecha de vencimiento
    let dueDateStr;
    if (typeof dueDate === 'string') {
      dueDateStr = dueDate.split('T')[0]; // Extraer solo la fecha si viene con hora
    } else {
      dueDateStr = new Date(dueDate).toISOString().split('T')[0];
    }
    
    // Comparar con hoy
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const todayDate = new Date(todayStr + 'T00:00:00');
    const dueDateParsed = new Date(dueDateStr + 'T00:00:00');
    const diffTime = dueDateParsed - todayDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Solo vencido si es estrictamente menor (diffDays < 0)
    return diffDays < 0;
  };

  // Obtener pagos vencidos
  const { data: overduePayments, isLoading: overdueLoading } = useQuery(
    'payment-alerts-overdue',
    async () => {
      try {
        const response = await api.get('/payments/overdue', { params: { limit: 50 } });
        const allOverdue = response.data || [];
        // Filtrar en el frontend para asegurar que solo sean realmente vencidos (fecha anterior a hoy)
        return allOverdue.filter(p => isOverdue(p.due_date, p.status));
      } catch (err) {
        console.error('Error al cargar pagos vencidos:', err);
        return [];
      }
    },
    {
      enabled,
      refetchInterval,
      staleTime: 2 * 60 * 1000, // Considerar datos frescos por 2 minutos
    }
  );

  // Obtener pagos próximos a vencer (próximos 3 días)
  const { data: dueSoonPayments, isLoading: dueSoonLoading } = useQuery(
    'payment-alerts-due-soon',
    async () => {
      try {
        const response = await api.get('/payments/due-soon', { 
          params: { days_ahead: 3, limit: 50 } 
        });
        const allDueSoon = response.data || [];
        // Filtrar para excluir pagos que ya están vencidos
        // Incluir pagos que vencen hoy (0 días) y en los próximos 3 días (1-3 días)
        return allDueSoon.filter(p => {
          if (p.status === 'paid' || p.status === 'cancelled') return false;
          // Verificar que no esté vencido
          if (isOverdue(p.due_date, p.status)) return false;
          // Calcular días hasta vencimiento
          let dueDateStr;
          if (typeof p.due_date === 'string') {
            dueDateStr = p.due_date.split('T')[0];
          } else {
            dueDateStr = new Date(p.due_date).toISOString().split('T')[0];
          }
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];
          const todayDate = new Date(todayStr + 'T00:00:00');
          const dueDateParsed = new Date(dueDateStr + 'T00:00:00');
          const diffTime = dueDateParsed - todayDate;
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          // Incluir si vence hoy (0 días) o en los próximos 3 días (1-3 días)
          return diffDays >= 0 && diffDays <= 3;
        });
      } catch (err) {
        console.error('Error al cargar pagos próximos a vencer:', err);
        return [];
      }
    },
    {
      enabled,
      refetchInterval,
      staleTime: 2 * 60 * 1000,
    }
  );

  const isLoading = overdueLoading || dueSoonLoading;
  
  // Asegurar que los arrays no sean null/undefined
  const overdue = overduePayments || [];
  const dueSoon = dueSoonPayments || [];
  const totalAlerts = overdue.length + dueSoon.length;

  return {
    overdue,
    dueSoon,
    totalAlerts,
    isLoading,
    hasAlerts: totalAlerts > 0,
  };
};

